import React, { useEffect, useMemo, useState } from "react";
import { SearchableSelect as SelectM } from "@/lib/shadcn/components/searchable-select";
import { ApartmentStats, Convert, GetMonthlyApartmentStatsResponse, PayoutStat } from "@/models/responses";
import { overViewIncomeFormatter } from "@/utils/formatters";
import { useAppData } from "@/hooks/useAppData";
import { useActiveApartmentEntities, useActiveApartmentOptions } from "@/store/slices/apartments";
import { Card, CardContent, CardHeader } from "@/lib/shadcn/ui/card";
import GroupMetricChart from "@/pages/graphs/group-metric-chart";
import TotalDepositsChart from "@/pages/graphs/total-deposits-chart";
import GroupYearOverYearChart from "@/pages/graphs/group-year-over-year-chart";
import { DatePicker } from "@/lib/shadcn/components/date-picker";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { aggregateApartmentStatsByGroup } from "@/utils/groupStatsAggregation";

interface GraphData {
    date: string;

    [key: string]: string | number;
}

export type Metric = "grossIncome" | "netIncome" | "occupancy" | "numberOfVisits" | "grossAdr" | "netAdr" | "averageBlt" | "averageLengthOfStay";

const metricOptions: {
    label: string;
    value: Metric;
    formatter: (value: number) => string;
}[] = [
    {
        label: "Gross Income",
        value: "grossIncome",
        formatter: overViewIncomeFormatter,
    },
    {
        label: "Net Income",
        value: "netIncome",
        formatter: overViewIncomeFormatter,
    },
    {
        label: "Gross ADR",
        value: "grossAdr",
        formatter: overViewIncomeFormatter,
    },
    { label: "Net ADR", value: "netAdr", formatter: overViewIncomeFormatter },
    {
        label: "Average BLT",
        value: "averageBlt",
        formatter: (value: number) => value.toFixed(1),
    },
    {
        label: "Average Length of Stay",
        value: "averageLengthOfStay",
        formatter: (value: number) => value.toFixed(1),
    },
    {
        label: "Occupancy",
        value: "occupancy",
        formatter: (value: number) => value.toFixed(0) + "%",
    },
    {
        label: "Number of Visits",
        value: "numberOfVisits",
        formatter: (value: number) => value.toFixed(0),
    },
];
const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Traceability: FR IDs reference
// .verified/features/apartment-filters-graphs-and-database/spec.md. Rewired off
// `/api/admin/groups/stats-by-month` onto the per-apartment endpoint;
// per-group stats are aggregated client-side with the filter applied (FR-008).
export const GroupGraphsPage: React.FC = () => {
    const [selectedMetric, setSelectedMetric] = React.useState<{
        label: string;
        value: Metric;
        formatter: (value: number) => string;
    }>(metricOptions[0]);
    const now = new Date();
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    const [startDate, setStartDate] = useState<Date>(oneYearAgo);
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 1));
    const [apartmentStats, setApartmentStats] = useState<ApartmentStats[]>([]);
    const [months, setMonths] = useState<Date[]>([]);
    const [totalDeposits, setTotalDeposits] = useState<
        {
            "Total Deposit": number;
            date: string;
        }[]
    >([]);
    const { groupIdMap } = useAppData();
    const { user } = useAuth();
    const { predicates } = useApartmentFilters();
    const apartmentEntities = useActiveApartmentEntities();
    const apartmentOptions = useActiveApartmentOptions();

    // Full active apartment-id list (minus "All"), passed to
    // `apartments/stats-by-month`. Predicate filtering happens client-side in
    // `aggregateApartmentStatsByGroup`, not here (FR-008, FR-014).
    const activeApartmentIds = useMemo(() => {
        return apartmentOptions
            .filter((opt) => opt.value !== "0")
            .map((opt) => Number(opt.value))
            .filter((n) => !Number.isNaN(n));
    }, [apartmentOptions]);

    // Fetch per-apartment monthly stats from `apartments/stats-by-month`
    // (was `groups/stats-by-month`; FR-008 rewire).
    useEffect(() => {
        if (!user || activeApartmentIds.length === 0) {
            return;
        }

        const apartmentsParam = activeApartmentIds.join(",");
        const apartmentStatsUrl = `${apiUrl}${getApiBasePath()}/${user.isAdmin ? "admin/" : ""}apartments/stats-by-month?apartments=${apartmentsParam}&startDate=${startDate.toISOString().slice(0, 10)}&endDate=${endDate.toISOString().slice(0, 10)}`;

        fetch(apartmentStatsUrl, fetchOptions)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.text();
            })
            .then((data) => {
                const stats: GetMonthlyApartmentStatsResponse = Convert.toGetMonthlyApartmentStatsResponse(data);
                setApartmentStats(stats.apartmentStats);
                setMonths(stats.months);
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: `Failed to fetch apartment stats: ${error.message}`,
                    variant: "destructive",
                });
            });

        fetch(`${apiUrl}/api/admin/total-payouts-by-month`, fetchOptions)
            .then((response) => response.text())
            .then((data) => {
                const payoutStats: PayoutStat[] = Convert.toGetPayoutStatsResponse(data).payoutStats;

                const deposits = payoutStats
                    .filter((payout) => {
                        const payoutDate = new Date(payout.fromDate);
                        return payoutDate >= startDate && payoutDate <= endDate;
                    })
                    .map((payout) => ({
                        date: `${monthNamesShort[payout.fromDate.getMonth()]} '${payout.fromDate.getFullYear().toString().substr(-2)}`,
                        "Total Deposit": payout.cumulativeDeposit,
                    }));
                setTotalDeposits(deposits);
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: `Failed to fetch total deposits: ${error.message}`,
                    variant: "destructive",
                });
            });
    }, [startDate, endDate, user, activeApartmentIds]);

    // Per-apartment monthly stats → per-group chart rows via the aggregation
    // helper (predicate narrowing, group bucketing, weighted averages — see
    // `utils/groupStatsAggregation.ts`). Memoised for a stable reference,
    // sidestepping the pre-rewire effect-based shape.
    const graphData = useMemo<GraphData[]>(() => {
        if (apartmentStats.length === 0 || months.length === 0) {
            return [];
        }

        const aggregated = aggregateApartmentStatsByGroup(apartmentStats, months, apartmentEntities, predicates);

        const statsByDate = new Map<string, Record<string, number>>();
        aggregated.forEach((row) => {
            const date = months[row.monthIndex];
            if (!date) return;
            const dateStr = `${monthNamesShort[date.getMonth()]} '${date.getFullYear().toString().substr(-2)}`;
            const groupName = groupIdMap.get(row.groupId)?.name || "Unknown";
            if (!statsByDate.has(dateStr)) {
                statsByDate.set(dateStr, {});
            }
            statsByDate.get(dateStr)![groupName] = row[selectedMetric.value];
        });

        return Array.from(statsByDate.entries()).map(([date, values]) => ({
            date,
            ...values,
        }));
    }, [apartmentStats, months, apartmentEntities, predicates, selectedMetric, groupIdMap]);

    function onMetricChanged(val: string) {
        metricOptions.forEach((option) => {
            if (option.value === val) {
                setSelectedMetric(option);
            }
        });
    }

    return (
        <>
            <Card className="shadow-sm max-w-[92vw] overflow-x-auto" data-testid="group-graphs-page">
                <CardHeader className="overflow-visible">
                    <ApartmentFilterBar />
                    <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-2 sm:gap-4">
                        <DatePicker onChange={setStartDate} selectedValue={startDate} variant={"volt"} />
                        <DatePicker onChange={setEndDate} selectedValue={endDate} variant={"volt"} />
                        <SelectM
                            variant="volt"
                            onChange={onMetricChanged}
                            placeholder="Metric"
                            selectedValue={selectedMetric.value}
                            options={metricOptions}
                            className="w-auto"
                            selectedValuePrefix={"Metric"}
                        />
                    </div>
                </CardHeader>

                <CardContent className="flex flex-col space-y-6 px-6">
                    {/* Group Metrics Chart */}
                    <div data-testid="group-graphs-chart">
                        <GroupMetricChart data={graphData} metric={selectedMetric.label} formatter={selectedMetric.formatter} />
                    </div>

                    {/* Total Deposits Chart */}
                    <TotalDepositsChart data={totalDeposits} formatter={overViewIncomeFormatter} />
                </CardContent>
            </Card>
            <Card className={"mt-6"}>
                <CardContent>
                    <GroupYearOverYearChart selectedMetric={selectedMetric} startDate={startDate} endDate={endDate} />
                </CardContent>
            </Card>
        </>
    );
};
